import axios from "axios";
import type { FilterOption } from "@/components/BlotterFilterBar";
import type {
  OrgAllowances,
  Organization,
  OrgCreditSettingsPatch,
  OrgListParams,
  OrgListResponse,
  OrgPatch,
  OrgSettingsPatch,
} from "@/interfaces/organization";
import { apiClient } from "@/lib/apiClient";
import { orgListQuery } from "@/utils/usersOrgs-helper";

export async function fetchOrganizations(
  params?: OrgListParams & {
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page?: number;
    limit?: number;
  },
): Promise<OrgListResponse> {
  const { sortBy, sortDir, page, limit, ...filterParams } = params ?? {};
  const query: Record<string, string> = orgListQuery(filterParams);
  if (sortBy) query.sortBy = sortBy;
  if (sortDir) query.sortDir = sortDir;
  if (page) query.page = String(page);
  if (limit) query.limit = String(limit);

  const { data } = await apiClient.get<OrgListResponse>("/admin/organizations", { params: query });
  return data;
}

/** Options for the Organization filter field (D4): all orgs the caller may see, sorted by name. */
export async function fetchOrganizationFilterOptions(): Promise<FilterOption[]> {
  const { data } = await apiClient.get<{
    success: boolean;
    data: { organizations: FilterOption[] };
  }>("/admin/organizations/filter-options");
  return data.data.organizations;
}

/** Fetches one organization's Detail row (scoped server-side to what the caller may view). */
export async function fetchOrganization(id: number): Promise<Organization> {
  const { data } = await apiClient.get<{ success: boolean; data: Organization }>(
    `/admin/organizations/${id}`,
  );
  return data.data;
}

/** Creates an organization (+ its root group, server-side). GFG-only. Returns the new id. */
export async function createOrganization(name: string): Promise<number> {
  try {
    const { data } = await apiClient.post<{ success: boolean; id: number }>(
      "/admin/organizations",
      { name },
    );
    return data.id;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error
      ? error
      : new Error("Failed to create organization. Please try again.");
  }
}

/** Renames an organization (name only; a Client Admin may rename their own org). */
export async function updateOrganization(id: number, patch: OrgPatch): Promise<void> {
  try {
    await apiClient.patch(`/admin/organizations/${id}`, patch);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error
      ? error
      : new Error("Failed to update organization. Please try again.");
  }
}

/** Updates GFG-only org settings (account lead / lifecycle). GFG only. */
export async function updateOrganizationSettings(
  id: number,
  patch: OrgSettingsPatch,
): Promise<void> {
  try {
    await apiClient.patch(`/admin/organizations/${id}/settings`, patch);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error
      ? error
      : new Error("Failed to update organization settings. Please try again.");
  }
}

/** The Allowances tab's read — terms in force, itemised exposure, the pending proposal, history. */
export async function fetchOrganizationAllowances(id: number): Promise<OrgAllowances> {
  const res = await apiClient.get(`/admin/organizations/${id}/allowances`);
  return res.data.data as OrgAllowances;
}

/** PROPOSES a change to the org's credit policy — limit, override, postpaid. Super-admin only. */
export async function updateOrganizationCreditSettings(
  id: number,
  patch: OrgCreditSettingsPatch,
): Promise<void> {
  try {
    await apiClient.patch(`/admin/organizations/${id}/credit-settings`, patch);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error as string);
    }
    throw error instanceof Error
      ? error
      : new Error("Failed to update the credit policy. Please try again.");
  }
}
